import React, { useState, useEffect } from 'react';
import { BarChart, PieChart, MessageSquare, TrendingUp } from 'lucide-react';
import { backendUrl } from '../../utils/socketClient';

interface SurveyAnalyticsProps {
  shopName: string;
  dateRange: { start: Date; end: Date };
  isLoading: boolean;
}

interface AnalyticsData {
  period_days: number;
  total_responses: number;
  avg_food_rating: number;
  avg_service_rating: number;
  avg_atmosphere_rating: number;
  avg_overall_rating: number;
  recent_general_feedback: string[];
  popular_dishes: string[];
  service_feedback: string[];
  atmosphere_feedback: string[];
  rating_distribution: {
    food: Record<string, number>;
    service: Record<string, number>;
    atmosphere: Record<string, number>;
  };
  age_distribution: Record<string, number>;
  gender_distribution: Record<string, number>;
  party_size_distribution: Record<string, number>;
  visit_purpose_distribution: Record<string, number>;
  time_period_distribution: Record<string, number>;
  budget_distribution: Record<string, number>;
}

const SurveyAnalytics: React.FC<SurveyAnalyticsProps> = ({ 
  shopName, 
  dateRange, 
  isLoading 
}) => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (shopName && !isLoading) {
      fetchAnalytics();
    }
  }, [shopName, dateRange, isLoading]);

  const fetchAnalytics = async () => {
    if (!shopName) return;

    setLoading(true);
    try {
      const daysDiff = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
      const response = await fetch(`${backendUrl}/survey-analytics/${encodeURIComponent(shopName)}?period=${daysDiff}`);
      
      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        console.error('Analytics fetch failed:', response.status);
      }
    } catch (error) {
      console.error('Analytics fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || isLoading) {
    return (
      <div className="space-y-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg shadow p-6">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">アンケートデータがありません</h3>
        <p className="text-gray-500">
          アンケート回答データが見つかりませんでした。
          <br />
          顧客にアンケートQRコードを案内して、データを収集してください。
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPIカード */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <TrendingUp className="h-12 w-12 text-blue-600" />
            </div>
            <div className="ml-6 w-0 flex-1">
              <dl>
                <dt className="text-lg font-medium text-gray-500 truncate">
                  回答数
                </dt>
                <dd className="text-3xl font-bold text-gray-900 mt-1">
                  {analytics.total_responses}件
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-8">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-yellow-600 font-bold text-2xl">⭐</span>
              </div>
            </div>
            <div className="ml-6 w-0 flex-1">
              <dl>
                <dt className="text-lg font-medium text-gray-500 truncate">
                  総合満足度
                </dt>
                <dd className="text-3xl font-bold text-gray-900 mt-1">
                  {analytics.avg_overall_rating ? analytics.avg_overall_rating.toFixed(1) : 'N/A'}点
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* 基本情報分析 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            年代分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.age_distribution || {}).map(([age, count]) => (
              <div key={age} className="flex justify-between text-sm">
                <span>{age}</span>
                <span className="font-medium">{count}人</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            性別分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.gender_distribution || {}).map(([gender, count]) => (
              <div key={gender} className="flex justify-between text-sm">
                <span>{gender}</span>
                <span className="font-medium">{count}人</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <BarChart className="h-5 w-5 mr-2" />
            利用人数分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.party_size_distribution || {}).map(([size, count]) => (
              <div key={size} className="flex justify-between text-sm">
                <span>{size}</span>
                <span className="font-medium">{count}件</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* 利用状況分析 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            利用用途分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.visit_purpose_distribution || {}).map(([purpose, count]) => (
              <div key={purpose} className="flex justify-between text-sm">
                <span>{purpose}</span>
                <span className="font-medium">{count}件</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <PieChart className="h-5 w-5 mr-2" />
            利用時間帯分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.time_period_distribution || {}).map(([time, count]) => (
              <div key={time} className="flex justify-between text-sm">
                <span>{time}</span>
                <span className="font-medium">{count}件</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <BarChart className="h-5 w-5 mr-2" />
            予算分布
          </h3>
          <div className="space-y-2">
            {Object.entries(analytics.budget_distribution || {}).map(([budget, count]) => (
              <div key={budget} className="flex justify-between text-sm">
                <span>{budget}</span>
                <span className="font-medium">{count}件</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 評価分析 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6 flex items-center">
          <BarChart className="h-5 w-5 mr-2" />
          カテゴリ別満足度（5点満点）
        </h3>
        <div className="space-y-6">
          {[
            { label: '料理', value: analytics.avg_food_rating, color: 'bg-green-500', borderColor: 'border-green-500' },
            { label: '接客', value: analytics.avg_service_rating, color: 'bg-blue-500', borderColor: 'border-blue-500' },
            { label: '雰囲気', value: analytics.avg_atmosphere_rating, color: 'bg-purple-500', borderColor: 'border-purple-500' }
          ].map((item) => (
            <div key={item.label} className="flex items-center">
              <div className="w-16 text-sm font-medium text-gray-700 flex-shrink-0">
                {item.label}
              </div>
              <div className="flex-1 mx-4">
                <div className="relative">
                  {/* 5点満点のベース */}
                  <div className="w-full bg-gray-200 rounded-lg h-8 border border-gray-300">
                    {/* 実際の評価値 */}
                    <div
                      className={`h-full rounded-lg ${item.color} flex items-center justify-end pr-2 transition-all duration-500`}
                      style={{ width: `${((item.value || 0) / 5) * 100}%` }}
                    >
                      <span className="text-white text-xs font-semibold">
                        {item.value ? item.value.toFixed(1) : '0.0'}
                      </span>
                    </div>
                    {/* 5点満点の目盛り */}
                    <div className="absolute top-0 left-0 w-full h-full flex">
                      {[1, 2, 3, 4, 5].map((point) => (
                        <div 
                          key={point} 
                          className="flex-1 border-r border-gray-400 last:border-r-0 flex items-center justify-center"
                        >
                          <span className="text-xs text-gray-600 font-medium">{point}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-20 text-right">
                <span className="text-lg font-bold text-gray-800">
                  {item.value ? item.value.toFixed(1) : 'N/A'}
                </span>
                <span className="text-sm text-gray-500 ml-1">/ 5.0</span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-xs text-gray-500 text-center">
          グラフ内の数字は満点（5点）の目盛り、右側の数字は実際の平均評価です
        </div>
      </div>

      {/* 特においしかった料理 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2" />
          特においしかった料理
        </h3>
        <div className="space-y-3">
          {analytics.popular_dishes && analytics.popular_dishes.length > 0 ? (
            analytics.popular_dishes.map((dish, index) => (
              <div key={index} className="p-3 bg-green-50 rounded-lg border-l-4 border-green-400">
                <p className="text-sm text-gray-700">"{dish}"</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-sm">料理のコメントがありません</p>
          )}
        </div>
      </div>

      {/* 接客の特筆事項 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MessageSquare className="h-5 w-5 mr-2" />
          接客の特筆事項
        </h3>
        <div className="space-y-3">
          {analytics.service_feedback && analytics.service_feedback.length > 0 ? (
            analytics.service_feedback.map((feedback, index) => (
              <div key={index} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                <p className="text-sm text-gray-700">"{feedback}"</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-sm">接客の特筆事項がありません</p>
          )}
        </div>
      </div>

      {/* 雰囲気の特筆事項 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MessageSquare className="h-5 w-5 mr-2" />
          雰囲気の特筆事項
        </h3>
        <div className="space-y-3">
          {analytics.atmosphere_feedback && analytics.atmosphere_feedback.length > 0 ? (
            analytics.atmosphere_feedback.map((feedback, index) => (
              <div key={index} className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                <p className="text-sm text-gray-700">"{feedback}"</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-sm">雰囲気の特筆事項がありません</p>
          )}
        </div>
      </div>

      {/* 最後：ご意見・ご感想 */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
          <MessageSquare className="h-5 w-5 mr-2" />
          ご意見・ご感想
        </h3>
        <div className="space-y-3">
          {analytics.recent_general_feedback && analytics.recent_general_feedback.length > 0 ? (
            analytics.recent_general_feedback.map((feedback, index) => (
              <div key={index} className="p-3 bg-teal-50 rounded-lg border-l-4 border-teal-400">
                <p className="text-sm text-gray-700">"{feedback}"</p>
              </div>
            ))
          ) : (
            <p className="text-gray-500 text-sm">ご意見・ご感想がありません</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyAnalytics;